<template>
  <div class="tournaments-page">
    <!-- Hero -->
    <section class="page-hero" style="background-image: var(--tkd-gradient-hero), url(/images/banner.jpg)">
      <div class="hero-text">
        <div class="hero-badge"><span class="badge-dot"></span>Турніри</div>
        <h1>Список турнірів</h1>
        <p>Створюйте турніри, керуйте учасниками та жеребкуванням.</p>
      </div>
      <router-link class="create-btn" :to="{path:'/user/tournaments/create'}">
        <span class="create-plus">+</span> Створити турнір
      </router-link>

      <div class="hero-stats" v-if="list">
        <div class="stat"><b>{{ list.length }}</b><span>усього</span></div>
        <div class="stat"><b>{{ myCount }}</b><span>моїх</span></div>
        <div class="stat"><b>{{ upcomingCount }}</b><span>майбутніх</span></div>
      </div>
      <div class="hero-flag"><span></span><span></span></div>
    </section>

    <!-- Toolbar -->
    <div class="toolbar">
      <div class="search">
        <span class="search-icon"><tkd-icon name="search"/></span>
        <input v-model.trim="search" type="search" placeholder="Пошук за назвою, містом або ID" aria-label="Пошук турнірів">
      </div>
      <div class="segmented" role="tablist">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          type="button"
          role="tab"
          :aria-selected="filter === tab.value ? 'true' : 'false'"
          :class="{ active: filter === tab.value }"
          @click="filter = tab.value"
        >{{ tab.label }}</button>
      </div>
    </div>

    <!-- List -->
    <div class="t-list" v-if="list">
      <article
        v-for="item in filtered"
        :key="item.id"
        class="t-card"
        :class="{ past: dateInfo(item.date).past }"
      >
        <div class="t-date">
          <template v-if="dateInfo(item.date).day">
            <b>{{ dateInfo(item.date).day }}</b>
            <span>{{ dateInfo(item.date).month }}</span>
            <small>{{ dateInfo(item.date).year }}</small>
          </template>
          <span v-else class="t-date-empty"><tkd-icon name="calendar"/></span>
        </div>

        <div class="t-body">
          <div class="t-tags">
            <span class="tag tag-id">#{{ item.id }}</span>
            <span class="tag tag-mine" v-if="isMine(item)">Мій</span>
            <span class="tag tag-upcoming" v-if="dateInfo(item.date).upcoming">Найближчий</span>
            <span class="tag tag-past" v-if="dateInfo(item.date).past">Завершений</span>
          </div>
          <h3 class="t-name" :title="item.name">
            <template v-if="item.name">{{ item.name }}</template>
            <span v-else class="missing">Назву не вказано</span>
          </h3>
          <div class="t-meta">
            <span><tkd-icon name="location"/> <template v-if="item.city">{{ item.city }}</template><span v-else class="missing">місто не вказано</span></span>
            <span><tkd-icon name="calendar"/> <template v-if="item.date">{{ item.date }}</template><span v-else class="missing">дату не вказано</span></span>
          </div>
        </div>

        <div class="t-actions" v-if="isMine(item)">
          <a :href="'/user/tournaments/overview/'+item.id" class="btn-open">Відкрити <tkd-icon name="arrow-right"/></a>
          <button type="button" class="btn-delete" @click="askDelete(item)" title="Видалити турнір" aria-label="Видалити турнір"><tkd-icon name="trash"/></button>
        </div>
      </article>

      <div class="empty" v-if="!filtered.length">
        <div class="empty-icon"><tkd-icon name="kyorugi"/></div>
        <template v-if="list.length">
          <b>Нічого не знайдено</b>
          <p>Спробуйте змінити пошук або фільтр.</p>
          <button type="button" class="link-btn" @click="search = ''; filter = 'all'">Скинути фільтри</button>
        </template>
        <template v-else>
          <b>Турнірів ще немає</b>
          <p>Створіть перший турнір, щоб почати роботу.</p>
          <router-link class="create-btn create-btn-inline" :to="{path:'/user/tournaments/create'}">
            <span class="create-plus">+</span> Створити турнір
          </router-link>
        </template>
      </div>
    </div>

    <div class="t-list" v-else>
      <div class="t-card skeleton-card" v-for="n in 4" :key="n">
        <div class="skeleton sk-date"></div>
        <div class="t-body">
          <div class="skeleton sk-tag"></div>
          <div class="skeleton sk-title"></div>
          <div class="skeleton sk-line"></div>
        </div>
      </div>
    </div>

    <!-- Delete confirm -->
    <transition name="fade">
      <div class="modal-overlay" v-if="toDelete" @click.self="toDelete = null">
        <div class="modal-box" role="dialog" aria-modal="true">
          <div class="modal-icon"><tkd-icon name="trash"/></div>
          <h3>Видалити турнір?</h3>
          <p><b>{{ toDelete.name || ('#' + toDelete.id) }}</b> буде видалено разом з усіма даними. Цю дію не можна скасувати.</p>
          <div class="modal-actions">
            <button type="button" class="btn-cancel" @click="toDelete = null">Скасувати</button>
            <button type="button" class="btn-confirm" :disabled="deleting" @click="deleteTournament(toDelete.id)">
              {{ deleting ? 'Видалення…' : 'Видалити' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
import 'src/assets/css/tkd-theme.css';

const MONTHS = ['січ', 'лют', 'бер', 'кві', 'тра', 'чер', 'лип', 'сер', 'вер', 'жов', 'лис', 'гру'];

export default {
  data() {
    return {
      search: '',
      filter: 'all',
      tabs: [
        { value: 'all', label: 'Усі' },
        { value: 'mine', label: 'Мої' },
        { value: 'upcoming', label: 'Майбутні' },
        { value: 'past', label: 'Минулі' },
      ],
      toDelete: null,
      deleting: false
    }
  },
  computed: {
    tournaments() {
      return this.$store.getters['tournament/tournaments'];
    },
    list() {
      return this.tournaments && this.tournaments.data ? this.tournaments.data.list || [] : null;
    },
    userOne() {
      return this.$store.getters['user/userOne'];
    },
    userId() {
      return this.userOne && this.userOne.data && this.userOne.data.user ? this.userOne.data.user.id : null;
    },
    myCount() {
      return this.list.filter(this.isMine).length;
    },
    upcomingCount() {
      return this.list.filter(item => this.dateInfo(item.date).future).length;
    },
    filtered() {
      let query = this.search.toLowerCase();
      return this.list.filter(item => {
        if (this.filter === 'mine' && !this.isMine(item)) return false;
        if (this.filter === 'upcoming' && !this.dateInfo(item.date).future) return false;
        if (this.filter === 'past' && !this.dateInfo(item.date).past) return false;
        if (!query) return true;
        return [item.name, item.city, String(item.id)]
          .some(value => value && String(value).toLowerCase().includes(query));
      });
    }
  },
  mounted() {
    this.$store.dispatch('tournament/fetchTournaments');
  },
  methods: {
    isMine(item) {
      return this.userId != null && item.user == this.userId;
    },
    // підтримує "2026-05-12", "12.05.2026" та "12/05/2026"
    dateInfo(value) {
      if (!value) return {};
      let match = String(value).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
      let year, month, day;
      if (match) {
        [, year, month, day] = match.map(Number);
      } else {
        match = String(value).match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})/);
        if (!match) return {};
        [, day, month, year] = match.map(Number);
      }
      let date = new Date(year, month - 1, day);
      if (isNaN(date.getTime())) return {};
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      let diffDays = (date - today) / 86400000;
      return {
        day,
        month: MONTHS[month - 1],
        year,
        future: diffDays >= 0,
        upcoming: diffDays >= 0 && diffDays <= 30,
        past: diffDays < 0
      };
    },
    askDelete(item) {
      this.toDelete = item;
    },
    deleteTournament(id) {
      this.deleting = true;
      this.$store.dispatch('tournament/deleteTournament', {'id': id})
        .then(() => this.deletedTournament(id))
        .finally(() => {
          this.deleting = false;
          this.toDelete = null;
        });
    },
    deletedTournament(id) {
      this.$notify(
        {
          text: 'Турнір видалено',
          horizontalAlign: 'right',
          verticalAlign: 'top',
          duration: 10000,
          speed: 1000,
          type: 'success'
        })

      this.deleteFromList(id);
    },
    deleteFromList(id) {
      let index = this.list.findIndex(item => item.id === id);
      if (index !== -1) {
        this.list.splice(index, 1);
      }
    }
  }
}
</script>
<style scoped>
  .tournaments-page {
    width: 100%;
    max-width: 1100px;
    margin: 0 auto;
    padding: 30px 15px 40px;
    font-family: var(--tkd-font);
    color: var(--tkd-text);
    animation: rise 0.5s ease-out both;
  }

  /* Hero */
  .page-hero {
    position: relative;
    overflow: hidden;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 20px 28px;
    padding: 32px 36px 30px;
    border-radius: var(--tkd-radius-lg);
    background-color: var(--tkd-navy);
    background-position: center;
    background-size: cover;
    box-shadow: 0 20px 50px rgba(10, 31, 68, 0.25);
    color: #fff;
  }

  .hero-text { min-width: 0; flex: 1 1 360px; }

  .hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 5px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--tkd-yellow);
    background: rgba(255, 213, 0, 0.1);
    border: 1px solid rgba(255, 213, 0, 0.35);
  }

  .badge-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--tkd-yellow);
    animation: pulse 2s infinite;
  }

  .page-hero h1 {
    margin: 12px 0 6px;
    font-size: 34px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: #fff;
  }

  .page-hero p { margin: 0; font-size: 15px; color: rgba(255, 255, 255, 0.72); }

  .create-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    height: 50px;
    padding: 0 22px;
    border-radius: var(--tkd-radius);
    font-size: 16px;
    font-weight: 700;
    color: var(--tkd-navy);
    background: var(--tkd-yellow);
    box-shadow: 0 10px 24px rgba(255, 213, 0, 0.25);
    text-decoration: none;
    transition: transform 0.15s, box-shadow 0.2s;
  }

  .create-btn:hover { color: var(--tkd-navy); text-decoration: none; transform: translateY(-1px); box-shadow: 0 14px 30px rgba(255, 213, 0, 0.35); }
  .create-btn:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
  .create-plus { font-size: 22px; line-height: 1; }

  .hero-stats {
    display: flex;
    gap: 12px;
    width: 100%;
  }

  .stat {
    display: flex;
    align-items: baseline;
    gap: 8px;
    padding: 10px 16px;
    border-radius: var(--tkd-radius);
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.12);
  }

  .stat b { font-size: 22px; font-weight: 800; color: #fff; }
  .stat span { font-size: 13px; color: rgba(255, 255, 255, 0.65); }

  .hero-flag {
    position: absolute;
    left: 36px;
    bottom: 0;
    display: flex;
    width: 64px;
    height: 5px;
    border-radius: 3px 3px 0 0;
    overflow: hidden;
  }
  .hero-flag span { flex: 1; background: var(--tkd-blue); }
  .hero-flag span + span { background: var(--tkd-yellow); }

  /* Toolbar */
  .toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    margin: 24px 0 16px;
  }

  .search {
    position: relative;
    flex: 1 1 280px;
  }

  .search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 15px;
    opacity: 0.55;
  }

  .search input {
    width: 100%;
    height: 48px;
    padding: 0 16px 0 42px;
    font-size: 15px;
    color: var(--tkd-text);
    background: var(--tkd-surface);
    border: 1.5px solid var(--tkd-border);
    border-radius: var(--tkd-radius);
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .search input:focus { border-color: var(--tkd-blue); box-shadow: 0 0 0 4px rgba(0, 87, 183, 0.12); }

  .segmented {
    display: flex;
    gap: 4px;
    padding: 4px;
    border-radius: var(--tkd-radius);
    background: var(--tkd-surface);
    border: 1.5px solid var(--tkd-border);
    overflow-x: auto;
  }

  .segmented button {
    height: 38px;
    padding: 0 14px;
    border: 0;
    border-radius: 9px;
    background: transparent;
    font-size: 14px;
    font-weight: 600;
    color: var(--tkd-muted);
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
  }

  .segmented button:hover { color: var(--tkd-text); background: var(--tkd-bg); }
  .segmented button.active { color: #fff; background: var(--tkd-gradient-btn); box-shadow: 0 4px 12px rgba(0, 87, 183, 0.3); }
  .segmented button:focus-visible { outline: 2px solid var(--tkd-blue); outline-offset: 1px; }

  /* Cards */
  .t-list { display: flex; flex-direction: column; gap: 12px; }

  .t-card {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 18px 20px;
    background: var(--tkd-surface);
    border: 1px solid var(--tkd-border);
    border-radius: var(--tkd-radius-lg);
    box-shadow: var(--tkd-shadow);
    transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;
  }

  .t-card:hover { border-color: rgba(0, 87, 183, 0.35); box-shadow: 0 14px 34px rgba(10, 31, 68, 0.12); transform: translateY(-1px); }
  .t-card.past { opacity: 0.78; }
  .t-card.past:hover { opacity: 1; }

  .t-date {
    flex-shrink: 0;
    width: 70px;
    height: 76px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    background: var(--tkd-gradient-btn);
    color: #fff;
    line-height: 1.1;
  }

  .t-card.past .t-date { background: linear-gradient(135deg, #5b6478, #3d4456); }
  .t-date b { font-size: 26px; font-weight: 800; }
  .t-date span { font-size: 13px; font-weight: 600; text-transform: uppercase; color: var(--tkd-yellow); }
  .t-date small { font-size: 11px; color: rgba(255, 255, 255, 0.7); }
  .t-date-empty { font-size: 24px; }

  .t-body { flex: 1; min-width: 0; }

  .t-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 6px; }

  .tag {
    padding: 2px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.03em;
  }

  .tag-id { color: var(--tkd-muted); background: var(--tkd-bg); }
  .tag-mine { color: var(--tkd-blue); background: rgba(0, 87, 183, 0.1); }
  .tag-upcoming { color: #8a6d00; background: rgba(255, 213, 0, 0.25); }
  .tag-past { color: #5b6478; background: #eceff4; }

  .t-name {
    margin: 0 0 6px;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.3;
    color: var(--tkd-text);
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .t-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 18px;
    font-size: 14px;
    color: var(--tkd-muted);
  }

  .missing { color: #b42318; font-style: italic; }

  .t-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

  .btn-open {
    display: inline-flex;
    align-items: center;
    height: 42px;
    padding: 0 18px;
    border-radius: var(--tkd-radius);
    font-size: 15px;
    font-weight: 700;
    color: #fff;
    background: var(--tkd-gradient-btn);
    box-shadow: 0 8px 18px rgba(0, 87, 183, 0.28);
    text-decoration: none;
    white-space: nowrap;
    transition: box-shadow 0.2s, transform 0.15s;
  }

  .btn-open:hover { color: #fff; text-decoration: none; transform: translateY(-1px); box-shadow: 0 12px 24px rgba(0, 87, 183, 0.38); }

  .btn-delete {
    width: 42px;
    height: 42px;
    border: 1.5px solid var(--tkd-border);
    border-radius: var(--tkd-radius);
    background: var(--tkd-surface);
    font-size: 16px;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
  }

  .btn-delete:hover { border-color: #f04438; background: #fef3f2; }
  .btn-open:focus-visible, .btn-delete:focus-visible { outline: 2px solid var(--tkd-blue); outline-offset: 2px; }

  /* Empty */
  .empty {
    padding: 48px 20px;
    text-align: center;
    background: var(--tkd-surface);
    border: 1.5px dashed var(--tkd-border);
    border-radius: var(--tkd-radius-lg);
  }

  .empty-icon { font-size: 40px; margin-bottom: 8px; }
  .empty b { font-size: 18px; }
  .empty p { margin: 6px 0 16px; color: var(--tkd-muted); }
  .create-btn-inline { box-shadow: none; }

  .link-btn {
    border: 0;
    background: none;
    color: var(--tkd-blue);
    font-weight: 600;
    cursor: pointer;
  }

  .link-btn:hover { text-decoration: underline; }

  /* Skeleton */
  .skeleton-card:hover { transform: none; border-color: var(--tkd-border); box-shadow: var(--tkd-shadow); }
  .skeleton {
    border-radius: 8px;
    background: linear-gradient(90deg, rgba(0, 0, 0, 0.06) 25%, rgba(0, 0, 0, 0.12) 50%, rgba(0, 0, 0, 0.06) 75%);
    background-size: 200% 100%;
    animation: shimmer 1.2s infinite;
  }
  .sk-date { width: 70px; height: 76px; border-radius: 14px; flex-shrink: 0; }
  .sk-tag { width: 60px; height: 16px; border-radius: 999px; margin-bottom: 10px; }
  .sk-title { width: 55%; height: 20px; margin-bottom: 10px; }
  .sk-line { width: 35%; height: 14px; }

  /* Modal */
  .modal-overlay {
    position: fixed;
    z-index: 2000;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(6, 16, 38, 0.6);
    backdrop-filter: blur(3px);
  }

  .modal-box {
    width: 100%;
    max-width: 420px;
    padding: 28px 24px 22px;
    border-radius: var(--tkd-radius-lg);
    background: var(--tkd-surface);
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);
    text-align: center;
  }

  .modal-icon {
    width: 56px;
    height: 56px;
    margin: 0 auto 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 24px;
    background: #fef3f2;
  }

  .modal-box h3 { margin: 0 0 8px; font-size: 20px; font-weight: 800; color: var(--tkd-text); }
  .modal-box p { margin: 0 0 20px; font-size: 15px; line-height: 1.5; color: var(--tkd-muted); overflow-wrap: anywhere; }
  .modal-actions { display: flex; gap: 10px; }

  .modal-actions button {
    flex: 1;
    height: 46px;
    border-radius: var(--tkd-radius);
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
  }

  .btn-cancel { border: 1.5px solid var(--tkd-border); background: var(--tkd-surface); color: var(--tkd-text); }
  .btn-cancel:hover { background: var(--tkd-bg); }
  .btn-confirm { border: 0; background: #d92d20; color: #fff; }
  .btn-confirm:hover:not(:disabled) { background: #b42318; }
  .btn-confirm:disabled { opacity: 0.6; cursor: not-allowed; }

  .fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
  .fade-enter, .fade-leave-to { opacity: 0; }

  @keyframes rise { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
  @keyframes shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }
  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(255, 213, 0, 0.6); }
    70% { box-shadow: 0 0 0 8px rgba(255, 213, 0, 0); }
    100% { box-shadow: 0 0 0 0 rgba(255, 213, 0, 0); }
  }

  @media (max-width: 767px) {
    .page-hero { padding: 26px 20px 26px; }
    .page-hero h1 { font-size: 28px; }
    .create-btn { width: 100%; justify-content: center; }
    .hero-stats { gap: 8px; }
    .stat { flex: 1; flex-direction: column; align-items: center; gap: 0; padding: 8px; }
    .hero-flag { left: 20px; }

    .t-card { flex-wrap: wrap; gap: 14px; padding: 16px; }
    .t-date { width: 58px; height: 64px; }
    .t-date b { font-size: 22px; }
    .t-body { flex-basis: calc(100% - 72px); }
    .t-actions { width: 100%; }
    .btn-open { flex: 1; justify-content: center; }
  }

  @media (prefers-reduced-motion: reduce) {
    .tournaments-page, .badge-dot, .skeleton { animation: none; }
  }
</style>
